export type ChainId = "solana" | "base" | "ethereum" | "robinhood" | "bnb" | "ink" | "arc";

export type SolanaDexKind =
  | "raydium_amm_v4"
  | "raydium_clmm"
  | "raydium_cpmm"
  | "raydium_launchlab"
  | "meteora_dlmm"
  | "meteora_damm_v1"
  | "meteora_damm_v2"
  | "meteora_dbc"
  | "pumpfun_bc"
  | "pumpswap";

/**
 * EVM DEX kinds — superset across all supported EVM chains. Per-chain
 * availability differs: Base has V2/V3/V4/Aerodrome; Ethereum has V2/V3/V4/Sushi;
 * Robinhood has Uniswap V2/V3/V4; BNB supports Pancake V2/V3 and
 * Uniswap V3.
 * The PoolPicker and validation gate per chain.
 */
export type EvmDexKind =
  | "uniswap_v3"
  | "uniswap_v4"
  | "pons_curve"
  | "lift_v2"
  | "full_fun"
  | "aerodrome"
  | "uniswap_v2"
  | "sushiswap"
  | "pancakeswap_v2"
  | "pancakeswap_v3"
  | "inkypump_v2"
  | "tsunami_curve"
  | "tsunami_v4";
/** @deprecated — prefer EvmDexKind. */
export type BaseDexKind = EvmDexKind;

export type DexKind = SolanaDexKind | EvmDexKind;

export type TaskAction = "buy" | "sell";
export type TaskStatus = "stopped" | "running" | "error";

/**
 * Pool reference. Storage shape is chain-specific:
 *   - Solana: `{ account, dex }` (base58 pool pubkey)
 *   - Base:   `{ address, dex }` (0x-prefixed pool address)
 * UI components should read whichever key is present (use the helper below).
 */
export type TaskPoolRef = {
  account?: string;
  address?: string;
  dex: DexKind;
  /** V4 only — 32-byte poolId (64-hex). Required when `dex === "uniswap_v4"`. */
  poolId?: string;
  /** InkyPump V2 bonding-curve launch identifier. */
  launchId?: string;
  /** Full.fun bonding-curve pool identifier. */
  fullFunPoolId?: string;
  /** Optional immutable V4 PoolKey metadata returned by discovery. */
  currency0?: string;
  currency1?: string;
  fee?: number;
  tickSpacing?: number;
  hooks?: string;
  /**
   * Solana — quote-token mint (WSOL, USDC, or custom like `either`). When
   * present, counter-trade buys/sells in this token directly via Jupiter.
   * When absent (legacy), defaults to WSOL.
   */
  quoteMint?: string;
  /** Solana — decimals of quoteMint (optional cache hint from discovery). */
  quoteDecimals?: number;
};

export function poolAddress(p: TaskPoolRef): string {
  return p.account ?? p.address ?? "";
}

/**
 * Identifier used in UI tables/keys. For V4 pools, the on-chain identifier
 * is the poolId (PoolManager is singleton). Falls back to address for V2/V3.
 */
export function poolIdentifier(p: TaskPoolRef): string {
  if ((p.dex === "uniswap_v4" || p.dex === "lift_v2" || p.dex === "tsunami_v4") && p.poolId) return p.poolId;
  if (p.dex === "inkypump_v2" && p.launchId) return p.launchId;
  if (p.dex === "full_fun" && p.fullFunPoolId) return p.fullFunPoolId;
  return poolAddress(p);
}

export type SendMode = "jito" | "rpc" | "jupiter" | "auto";

/**
 * EVM-only — execution venue selection (Phase 7 EVM). Task watches ALL its
 * seeded pools for triggers; this picks WHICH pool the counter-trade fires in:
 *
 *   "weth":    always route counter-trade to a WETH-paired pool in the task.
 *              Triggers from USDC pools still react — bot finds WETH pool from
 *              same task. Default.
 *   "usdc":    always route to a USDC-paired pool in the task. Wallets must
 *              hold USDC. Triggers from WETH pools route to USDC venue.
 *   "trigger": execute in the same pool that fired the trigger (follow signal).
 *   executionPool: execute through one exact pool address/poolId from the
 *                  token's configured pools. Overrides quoteMode when set.
 *
 * Legacy values "native" → "weth" and "pool" → "trigger" are accepted by
 * server for backward compat with pre-rename tasks.
 */
export type EvmQuoteMode = "weth" | "usdc" | "trigger" | "native" | "pool";

export type TaskConfig = {
  minTriggerUsd?: number;
  maxTriggerUsd?: number;
  mcRangeUsd?: [number, number];
  counterSizePctRange: [number, number];
  maxPositionSol?: number;
  maxTotalExposureSol?: number;
  maxPositionEth?: number;
  maxTotalExposureEth?: number;
  globalCooldownMs?: number;
  perWalletCooldownMs?: number;
  slippageBps: { known: number; volatile: number };
  ignoreWallets?: string[];
  sendMode?: SendMode;
  solanaSettlement?: "pool-quote" | "sol";
  quoteMode?: EvmQuoteMode;
  executionPool?: string;
  settleNative?: boolean;
  /** Exact WETH/quote bridge; omitted means auto-select by live quote. */
  nativeBridgePool?: string;
  /** EVM trigger grouping; unset = per-tx on Robinhood, per-block elsewhere. */
  aggregation?: "per-tx" | "per-block";
  minLegUsd?: number;
  maxGasPctOfTrade?: number;
  maxCounterUsdPerTraderPerHour?: number;
  priceLimitUsd?: number;
  /** Ethereum SELL tasks approve their sell venues at start unless false. */
  preApprove?: boolean;
  /** Ethereum: answer pending triggers with a backrun bundle. */
  mempoolExecution?: "bundle" | "send";
};

export type NativeBridgeCandidate = {
  address: string;
  dex: "uniswap_v2" | "uniswap_v3";
  feeBps: number;
  liquidityUsd: number | null;
  /** On-chain TVL estimate denominated in wrapped-native wei. */
  liquidityNativeWei: string | null;
  volume24hUsd: number | null;
  referenceOut: string;
  recommended: boolean;
};

export type Token = {
  id: string;
  chain: ChainId;
  mint: string;
  name: string;
  pools: TaskPoolRef[];
  archived: boolean;
  /** Number of tasks currently using this token. */
  tasksCount?: number;
  createdAt: number;
  updatedAt: number;
};

export type TaskTradeTotals = {
  buyUsd: number;
  sellUsd: number;
  buyCount: number;
  sellCount: number;
  estimatedSellCount: number;
};

export type Task = {
  id: string;
  name: string;
  chain: ChainId;
  /** FK to tokens — derives mint and pools. Always set on tasks created post-v3. */
  tokenId: string | null;
  /** Live token snapshot embedded by API (may be null on legacy rows). */
  token: Token | null;
  /** Legacy/denormalized — present in API DTO for backward compat. Prefer `token.mint`. */
  mint: string;
  action: TaskAction;
  /** Legacy/denormalized — prefer `token.pools`. */
  pools: TaskPoolRef[];
  config: TaskConfig;
  status: TaskStatus;
  lastError: string | null;
  dryRun: boolean;
  walletCount: number;
  /** Pubkeys of all wallets in DB (without private keys). Persists across stop/start. */
  walletPubkeys: string[];
  /** Lifetime confirmed countertrades, retained across stop/start and server restarts. */
  tradeTotals: TaskTradeTotals;
  createdAt: number;
  updatedAt: number;
};

export type PoolCandidate = {
  /**
   * For V2/V3/Aerodrome/Sushi — pool contract address (40-hex).
   * For V4 — 32-byte poolId (64-hex). Same value as `poolId` field below.
   */
  poolAccount: string;
  dex: DexKind;
  baseMint: string;
  quoteMint: string;
  liquidityUsd: number | null;
  volume24hUsd: number | null;
  priceUsd: number | null;
  /** V3 + V4 fee tier in hundredths of a bip (3000 = 0.3%). */
  fee?: number;
  // --- V4-only fields ---
  /** Same as poolAccount for V4 entries; convenience alias. */
  poolId?: string;
  /** V4 tickSpacing from PoolKey. */
  tickSpacing?: number;
  /** V4 hooks contract. Non-`0x000…000` value = pool has hook logic (warning). */
  hooks?: string;
  /** V4 PoolKey currencies supplied by InkyPump after migration. */
  currency0?: string;
  currency1?: string;
  /** InkyPump V2 curve launch identifier. */
  launchId?: string;
  /** Full.fun curve's sequential platform pool id. */
  fullFunPoolId?: string;
};

export type TaskStats = {
  uptimeSec: number;
  tx: number;
  decoded: number;
  buys: number;
  sells: number;
  perDex: Record<string, number>;
  triggers: {
    evaluated: number;
    approved: number;
    skipped: Record<string, number>;
  };
  exposureSol: number;
  /** EVM: recorded trades of this task (all runs). */
  trading?: {
    buys: number;
    sells: number;
    spentNative: number;
    boughtTokens: number;
    soldTokens: number;
    receivedNative: number;
    positionTokens: number;
    avgCostNative: number | null;
    realizedPnlNative: number | null;
    gasNative: number;
    unmeasured: number;
  };
  wallets: {
    label: string;
    pubkey: string;
    solSol: number;
    tokens: string;
    tokenDecimals: number;
    tokenUiAmount: number;
    tokenUsdValue: number | null;
    idleSec: number;
    /** Per-quote-mint balances (Solana, non-WSOL quotes only). */
    quotes?: {
      mint: string;
      raw: string;
      decimals: number;
      uiAmount: number;
      symbol?: string;
    }[];
  }[];
  solUsd: number | null;
  fdvUsd: number | null;
  mcInRange: boolean;
};

/** Public balance snapshot fetched on demand for a saved task wallet. */
export type WalletBalanceSnapshot = {
  pubkey: string;
  nativeBalance: number | null;
  tokenBalance: {
    amount: string;
    decimals: number;
    uiAmount: number;
  } | null;
};
